import type { ThemeSetting } from "./types"

/**
 * Layer 3 of the configuration: public `VITE_APP_*` variables.
 * Everything here ends up in the public bundle — never put secrets in it.
 */
function envValue(key: string): string | undefined {
  const env = import.meta.env as unknown as Record<string, unknown>
  const value = env[key]
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

function envList(key: string): string[] | undefined {
  const raw = envValue(key)
  if (!raw) return undefined
  const values = raw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean)
  return values.length > 0 ? values : undefined
}

function envFeatures(): Record<string, boolean> | undefined {
  const raw = envValue("VITE_APP_FEATURES")
  if (!raw) return undefined
  const features: Record<string, boolean> = {}
  for (const entry of raw.split(",")) {
    const [rawName, rawFlag] = entry.split("=")
    const name = rawName?.trim()
    if (!name) continue
    const flag = (rawFlag ?? "").trim().toLowerCase()
    features[name] = !(flag === "off" || flag === "false" || flag === "0" || flag === "disabled")
  }
  return features
}

function parseJsonEnv(key: string): unknown {
  const raw = envValue(key)
  if (!raw) return undefined
  try {
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

/** Builds a configuration patch from the public environment variables. */
export function readEnvPatch(): Record<string, unknown> {
  const patch: Record<string, unknown> = {}

  const branding: Record<string, unknown> = {}
  const brandingMap: Record<string, string> = {
    name: "VITE_APP_NAME",
    shortName: "VITE_APP_SHORT_NAME",
    description: "VITE_APP_DESCRIPTION",
    logo: "VITE_APP_LOGO",
    favicon: "VITE_APP_FAVICON",
    titleTemplate: "VITE_APP_TITLE_TEMPLATE",
    footerText: "VITE_APP_FOOTER_TEXT",
  }
  for (const [field, key] of Object.entries(brandingMap)) {
    const value = envValue(key)
    if (value) branding[field] = value
  }
  if (Object.keys(branding).length > 0) patch.branding = branding

  const design: Record<string, unknown> = {}
  const primary = envValue("VITE_APP_PRIMARY_COLOR")
  const accent = envValue("VITE_APP_ACCENT_COLOR")
  if (primary || accent) {
    design.colors = {
      ...(primary ? { primary } : {}),
      ...(accent ? { accent } : {}),
    }
  }
  const theme = envValue("VITE_APP_THEME") as ThemeSetting | undefined
  if (theme) design.theme = { default: theme }
  const radius = Number(envValue("VITE_APP_RADIUS"))
  if (Number.isFinite(radius) && radius > 0) design.radius = radius
  const density = envValue("VITE_APP_DENSITY")
  if (density === "compact" || density === "comfortable" || density === "spacious") {
    design.density = density
  }
  if (Object.keys(design).length > 0) patch.design = design

  const relays: Record<string, unknown> = {}
  const defaultRelays = envList("VITE_APP_DEFAULT_RELAYS")
  if (defaultRelays) relays.defaultRelays = defaultRelays
  const searchRelays = envList("VITE_APP_SEARCH_RELAYS")
  if (searchRelays) relays.searchRelays = searchRelays
  const trendingRelays = envList("VITE_APP_TRENDING_RELAYS")
  if (trendingRelays) relays.trendingRelays = trendingRelays
  const discoveryRelays = envList("VITE_APP_DISCOVERY_RELAYS")
  if (discoveryRelays) relays.discoveryRelays = discoveryRelays
  const collectionsUrl = envValue("VITE_APP_DISCOVERY_COLLECTIONS_URL")
  if (collectionsUrl) relays.discoveryCollectionsUrl = collectionsUrl
  if (Object.keys(relays).length > 0) patch.relays = relays

  const features = envFeatures()
  if (features) patch.features = features

  const auth: Record<string, unknown> = {}
  const googleClientId = envValue("VITE_APP_GOOGLE_CLIENT_ID")
  if (googleClientId !== undefined) auth.googleClientId = googleClientId
  const nip46Relays = envList("VITE_APP_NIP46_RELAYS")
  if (nip46Relays) auth.nip46Relays = nip46Relays
  const allowSignup = envValue("VITE_APP_ALLOW_SIGNUP")
  if (allowSignup !== undefined) auth.allowSignup = /^(1|true|yes|on)$/i.test(allowSignup)
  if (Object.keys(auth).length > 0) patch.auth = auth

  const language = envValue("VITE_APP_LANGUAGE")
  if (language) patch.i18n = { defaultLanguage: language }

  const communityMode = envValue("VITE_APP_COMMUNITY_MODE")
  const communityRelaySets = parseJsonEnv("VITE_APP_COMMUNITY_RELAY_SETS")
  const communityRelays = envList("VITE_APP_COMMUNITY_RELAYS")
  const community: Record<string, unknown> = {}
  if (communityMode !== undefined) community.mode = /^(1|true|yes|on)$/i.test(communityMode)
  if (Array.isArray(communityRelaySets)) community.relaySets = communityRelaySets
  if (communityRelays) community.extraRelays = communityRelays
  if (Object.keys(community).length > 0) patch.community = community

  return patch
}

export function runtimeConfigUrl(): string {
  const explicit = envValue("VITE_APP_CONFIG_URL")
  if (explicit) return explicit
  const base = import.meta.env.BASE_URL ?? "/"
  return `${base.endsWith("/") ? base : `${base}/`}app-config.json`
}
